import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({ selector: '.cta-chooser' })
export class CtaChooserDirective {
  constructor(private host: ElementRef<HTMLElement>) {}

  private get root() { return this.host.nativeElement; }
  private get toggle() { return this.root.querySelector<HTMLElement>('.cta-chooser-toggle'); }
  private get menu() { return this.root.querySelector<HTMLElement>('.cta-chooser-menu'); }
  private get items() { return Array.prototype.slice.call(this.menu ? this.menu.querySelectorAll('[role="menuitem"]') : []) as HTMLElement[]; }

  private get activeElement(): Element | null {
    const rootNode = this.root.getRootNode() as Document | ShadowRoot;
    return rootNode && (rootNode as any).activeElement ? (rootNode as any).activeElement : document.activeElement;
  }

  private open(focusFirst: boolean) {
    const toggle = this.toggle;
    if (!toggle || !this.menu) { return; }
    this.root.classList.add('open');
    toggle.setAttribute('aria-expanded', 'true');
    const items = this.items;
    if (focusFirst && items[0]) { items[0].focus(); }
  }

  private close(returnFocus?: boolean) {
    const toggle = this.toggle;
    if (!toggle || !this.root.classList.contains('open')) { return; }
    this.root.classList.remove('open');
    toggle.setAttribute('aria-expanded', 'false');
    if (returnFocus) { toggle.focus(); }
  }

  @HostListener('click', ['$event'])
  onClick(e: MouseEvent) {
    const toggle = this.toggle;
    if (!toggle || !this.menu || !toggle.contains(e.target as Node)) { return; }
    e.preventDefault();
    if (this.root.classList.contains('open')) { this.close(); } else { this.open(e.detail === 0); }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    const toggle = this.toggle;
    const menu = this.menu;
    if (!toggle || !menu) { return; }
    if (toggle.contains(e.target as Node)) {
      if (e.key === 'ArrowDown') { e.preventDefault(); this.open(true); }
      return;
    }
    if (!menu.contains(e.target as Node)) { return; }
    const items = this.items;
    const i = items.indexOf(this.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length].focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length].focus(); }
    else if (e.key === 'Escape') { e.preventDefault(); this.close(true); }
    else if (e.key === 'Tab') { this.close(); }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: Event) {
    const path = e.composedPath ? e.composedPath() : [];
    if (!path.includes(this.root)) { this.close(); }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') { this.close(true); }
  }
}
